import React, { forwardRef, HTMLProps } from 'react';
import styled from '@emotion/styled';
import shouldForwardProp from '@styled-system/should-forward-prop';
import PropTypes from 'prop-types';

import { BaseCallToAction } from '../../internal/components';
import sx from '../../internal/styles/sx';
import { ButtonProps, StyledButtonProps } from './types';
import base from './variants/base';
import circleVariant from './variants/circledVariant';
import fullWidthVariant from './variants/fullWidthVariant';
import sizeVariant from './variants/sizeVariant';
import typeVariant from './variants/typeVariant';

const StyledButton = styled(BaseCallToAction, {
  shouldForwardProp,
})<StyledButtonProps>(
  base,
  sizeVariant,
  typeVariant,
  fullWidthVariant,
  circleVariant,
  sx
);
// renaming prop type -> variant
// nothing is changing in the contract from outside
const Button = forwardRef<
  HTMLButtonElement,
  Omit<HTMLProps<HTMLButtonElement>, 'size' | 'ref'> & Omit<ButtonProps, 'ref'>
>(
  (
    {
      nativeType: type = 'button',
      type: variant = 'primary',
      inverted = false,
      children,
      size = 'medium',
      ...rest
    },
    ref
  ) => {
    return (
      <StyledButton
        type={type}
        variant={variant}
        inverted={inverted}
        size={size}
        ref={ref}
        {...rest}
      >
        {children}
      </StyledButton>
    );
  }
);

Button.propTypes = {
  disabled: PropTypes.bool,
  fullWidth: PropTypes.bool,
  inverted: PropTypes.bool,
  onClick: PropTypes.func,
  size: PropTypes.oneOf(['small', 'medium', 'large']),
  type: PropTypes.oneOf([
    'primary',
    'secondary',
    'text',
    'positive',
    'warning',
    'critical',
  ]),
  nativeType: PropTypes.oneOf(['submit', 'reset', 'button']),
};

export { StyledButton };
export default Button;
